import { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { alpha, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import { visuallyHidden } from "@mui/utils";
import axios from "axios";
import api from "../../api";
import { useDebounce } from "../../hooks/useDebounce";

// Search Imports
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";

import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import ListItemText from '@mui/material/ListItemText';
import Select from '@mui/material/Select';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

// ... Comparator Functions ...
function descendingComparator(a, b, orderBy) {
  // Handle nested properties or case insensitivity if needed
  let valA = a[orderBy];
  let valB = b[orderBy];

  // Specific check for Role Name normalization
  if (orderBy === 'role_name') {
    valA = a.role_name || a.name || "";
    valB = b.role_name || b.name || "";
  }

  if (typeof valA === 'string') valA = valA.toLowerCase();
  if (typeof valB === 'string') valB = valB.toLowerCase();

  if (valB < valA) return -1;
  if (valB > valA) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// ... Table Config ...
const userHeadCells = [
  { id: "name", numeric: false, disablePadding: true, label: "Name" },
  { id: "email", numeric: false, disablePadding: false, label: "Email" },
  { id: "roles", numeric: false, disablePadding: false, label: "Roles" },
  { id: "actions", numeric: false, disablePadding: false, label: "Edit" },
];

const roleHeadCells = [
  { id: "role_name", numeric: false, disablePadding: true, label: "Role Name" },
  { id: "permissions", numeric: false, disablePadding: false, label: "Permissions" },
  { id: "actions", numeric: false, disablePadding: false, label: "Actions" },
];

// ... Enhanced Table Head (Generic) ...
function EnhancedTableHead(props) {
  const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort, cells } = props;
  const createSortHandler = (property) => (event) => onRequestSort(event, property);

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
          />
        </TableCell>
        {cells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

// --- GENERIC TOOLBAR ---
function EnhancedTableToolbar(props) {
  const {
    numSelected,
    handleDeleteClick,
    searchQuery,
    setSearchQuery,
    handleAddClick,
    title,
    searchPlaceholder,
    showDelete // Added to control delete icon visibility
  } = props;

  return (
    <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 }, ...(numSelected > 0 && { bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity) }) }}>
      {numSelected > 0 ? (
        <Typography sx={{ flex: "1 1 100%" }} color="inherit" variant="subtitle1" component="div">{numSelected} selected</Typography>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
          <Typography sx={{ flexShrink: 0 }} variant="h6" id="tableTitle" component="div">{title}</Typography>

          <TextField
            variant="outlined"
            size="small"
            placeholder={searchPlaceholder}
            value={searchQuery || ""}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ ml: 'auto', width: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}
      {numSelected > 0 ? (
        showDelete && (
          <Tooltip title="Delete">
            <IconButton onClick={handleDeleteClick} color="inherit">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )
      ) : (
        <Tooltip title="Create">
          <IconButton onClick={handleAddClick} color="primary">
            <AddIcon />
          </IconButton>
        </Tooltip>
      )}
    </Toolbar>
  );
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export default function ManageUsers() {
  const theme = useTheme();

  // === USER STATE ===
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // === ROLE STATE (New) ===
  const [availableRoles, setAvailableRoles] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [roleSearchQuery, setRoleSearchQuery] = useState("");
  const [debouncedRoleSearchQuery] = useDebounce(roleSearchQuery, 300);
  const [roleOrder, setRoleOrder] = useState("asc");
  const [roleOrderBy, setRoleOrderBy] = useState("role_name");
  const [roleSelected, setRoleSelected] = useState([]);
  const [rolePage, setRolePage] = useState(0);
  const [roleRowsPerPage, setRoleRowsPerPage] = useState(10);

  // === DIALOG STATES ===
  const [dense, setDense] = useState(true);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [singleDeleteEmail, setSingleDeleteEmail] = useState(null);
  const [openRoleDeleteDialog, setOpenRoleDeleteDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editData, setEditData] = useState({ email: "", name: "", roles: [] });
  const [OpenCreatePermDialog, setOpenCreatePermDialog] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [createData, setCreateData] = useState({
    name: "",
    email: "",
    password: "",
    roles: []
  });
  const [createPerm, setCreatePerm] = useState({
    perm_name: "",
    perm_id: "",
    permission: ""
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [errorMessage, setErrorMessage] = useState(null);

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Role management specific dialogs
  const [openCreateRoleDialog, setOpenCreateRoleDialog] = useState(false);
  const [openEditRoleDialog, setOpenEditRoleDialog] = useState(false);
  const [roleData, setRoleData] = useState({ role_name: "", permissions: [] });
  const [editingRole, setEditingRole] = useState(null);

  // --- API CALLS ---
  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("accessToken");
      const response = await api.get("/api/manage-users/list", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(response.data.users || []);
      setIsLoading(false);
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Error fetching data");
      setIsLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      setIsLoading(true);
      const response = await api.get("/api/manage-users/roles/list");
      setAvailableRoles(response.data.roles || []);
      setIsLoading(false);
    } catch (err) {
      console.error("Failed to fetch roles", err);
      setSnackbar({ open: true, message: "Failed to fetch roles", severity: "error" });
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await api.get("/api/manage-users/roles/permissions");
      setAvailablePermissions(response.data.permissions || []);
    } catch (err) {
      console.error("Failed to fetch permissions", err);
      setSnackbar({ open: true, message: "Failed to fetch permissions", severity: "error" });
    }
  };

  {/*// Custom permission to show a permission creation dialog in the dropdown
  const AddNew = {
    perm_id: "",
    perm_name: "Make a new permission"
  };*/}

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchPermissions();
  }, []);

  // --- USER FILTER LOGIC ---
  const filteredRows = useMemo(() => {
    if (!debouncedSearchQuery) return rows;
    const lowerQuery = debouncedSearchQuery.toLowerCase();

    return rows.filter((row) => {
      const nameMatch = (row.name?.toLowerCase() || "").includes(lowerQuery);
      const emailMatch = (row.email?.toLowerCase() || "").includes(lowerQuery);

      let roleString = "";
      if (Array.isArray(row.roles)) {
        roleString = row.roles.join(" ");
      } else if (typeof row.roles === 'string') {
        roleString = row.roles;
      }
      const roleMatch = roleString.toLowerCase().includes(lowerQuery);

      return nameMatch || emailMatch || roleMatch;
    });
  }, [rows, debouncedSearchQuery]);

  // --- ROLE FILTER LOGIC ---
  const filteredRoles = useMemo(() => {
    if (!debouncedRoleSearchQuery) return availableRoles;
    const lowerQuery = debouncedRoleSearchQuery.toLowerCase();

    return availableRoles.filter((role) => {
      const roleName = (role.role_name || role.name || "").toLowerCase();

      return roleName.includes(lowerQuery);
    });
  }, [availableRoles, debouncedRoleSearchQuery, availablePermissions]);

  // Reset pages when searching
  useEffect(() => { setPage(0); }, [debouncedSearchQuery]);
  useEffect(() => { setRolePage(0); }, [debouncedRoleSearchQuery]);


  // --- USER TABLE HANDLERS ---
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = filteredRows.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };
  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) newSelected = newSelected.concat(selected, id);
    else if (selectedIndex === 0) newSelected = newSelected.concat(selected.slice(1));
    else if (selectedIndex === selected.length - 1) newSelected = newSelected.concat(selected.slice(0, -1));
    else if (selectedIndex > 0) newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    setSelected(newSelected);
  };
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };
  const isSelected = (id) => selected.indexOf(id) !== -1;

  // --- ROLE TABLE HANDLERS ---
  const handleRoleRequestSort = (event, property) => {
    const isAsc = roleOrderBy === property && roleOrder === "asc";
    setRoleOrder(isAsc ? "desc" : "asc");
    setRoleOrderBy(property);
  };
  const handleRoleSelectAllClick = (event) => {
    if (event.target.checked) {
      // Use role_id or id
      const newSelected = filteredRoles.map((n) => n.role_id || n.id);
      setRoleSelected(newSelected);
      return;
    }
    setRoleSelected([]);
  };
  const handleRoleClick = (event, id) => {
    const selectedIndex = roleSelected.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) newSelected = newSelected.concat(roleSelected, id);
    else if (selectedIndex === 0) newSelected = newSelected.concat(roleSelected.slice(1));
    else if (selectedIndex === roleSelected.length - 1) newSelected = newSelected.concat(roleSelected.slice(0, -1));
    else if (selectedIndex > 0) newSelected = newSelected.concat(roleSelected.slice(0, selectedIndex), roleSelected.slice(selectedIndex + 1));
    setRoleSelected(newSelected);
  };
  const handleChangeRolePage = (event, newPage) => setRolePage(newPage);
  const handleChangeRoleRowsPerPage = (event) => { setRoleRowsPerPage(parseInt(event.target.value, 10)); setRolePage(0); };
  const isRoleSelected = (id) => roleSelected.indexOf(id) !== -1;


  // --- USER EDIT LOGIC ---
  const handleEditClick = (e, user) => {
    let userRoles = [];
    if (Array.isArray(user.roles)) {
      userRoles = user.roles;
    } else if (typeof user.roles === 'string') {
      userRoles = [user.roles];
    }

    setEditData({
      email: user.email,
      name: user.name || "",
      roles: userRoles
    });
    setOpenEditDialog(true);
  };

  const handleEditRolesChange = (event) => {
    const { target: { value } } = event;
    setEditData({
      ...editData,
      roles: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleEditSave = async () => {
    const token = localStorage.getItem("accessToken");
    if (!editData.email) {
      setSnackbar({ open: true, message: "Error: No email found for user.", severity: "error" });
      return;
    }
    const rolesPayload = Array.isArray(editData.roles) ? editData.roles : [];

    try {
      const safeEmail = encodeURIComponent(editData.email);
      await api.put(`/api/manage-users/update-role/${safeEmail}`,
        { roles: rolesPayload },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      fetchUsers();
      setOpenEditDialog(false);
      setSnackbar({ open: true, message: "User updated successfully!", severity: "success" });
    } catch (err) {
      console.error("Edit failed:", err);
      const errMsg = err.response?.data?.message || "Failed to update user.";
      setSnackbar({ open: true, message: errMsg, severity: "error" });
    }
  };

  // --- CREATE USER LOGIC ---
  const handleAddClick = () => {
    setCreateData({ name: "", email: "", password: "", roles: [] }); // Reset form
    setOpenCreateDialog(true);
  };

  const handleCreateRolesChange = (event) => {
    const { target: { value } } = event;
    setCreateData((prev) => ({
      ...prev,
      roles: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleCreateClick = async () => {
    if (!createData.name || !createData.email || !createData.password) {
      setSnackbar({ open: true, message: "Please fill all required fields.", severity: "warning" });
      return;
    }
    const token = localStorage.getItem("accessToken");
    try {
      await api.post("/api/manage-users/create",
        createData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchUsers();
      setOpenCreateDialog(false);
      setCreateData({ name: "", email: "", password: "", roles: [] });
      setSnackbar({ open: true, message: "User created successfully!", severity: "success" });
    } catch (err) {
      console.error("Create failed:", err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Failed to create user.",
        severity: "error"
      });
    }
  };


  {/* Create Permission Dialog */ }
  /*
  const handleCreatePermClick = async () => {
    if (!createPerm.perm_id || !createPerm.permission || !createPerm.permission) {
      setSnackbar({ open: true, message: "Please fill all required fields.", severity: "warning" });
      return;
    }
    const token = localStorage.getItem("accessToken");
    try {
      await api.post("/api/manage-users/roles/createPerm",
        createPerm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchPermissions();
      setOpenCreatePermDialog(false);
      setCreatePerm({ perm_id: "", perm_name: "", permission: "", });
      setSnackbar({ open: true, message: "Permission created successfully!", severity: "success" });
    } catch (err) {
      console.error("Create failed:", err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Failed to create permission.",
        severity: "error"
      });
    }
  };*/

  // --- DELETE USER LOGIC ---
  const handleDeleteClick = () => { setSingleDeleteEmail(null); setOpenDeleteDialog(true); };

  const handleConfirmDelete = async () => {
    const token = localStorage.getItem("accessToken");
    let emailsToDelete = filteredRows.filter((r) => selected.includes(r.id)).map((r) => r.email);
    try {
      await Promise.all(emailsToDelete.map((email) => {
        const safeEmail = encodeURIComponent(email);
        return api.delete(`/api/manage-users/delete/${safeEmail}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }));
      fetchUsers();
      setSelected([]);
      setOpenDeleteDialog(false);
      setSnackbar({ open: true, message: "User(s) deleted successfully!", severity: "success" });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Failed to delete user(s).",
        severity: "error"
      });
      setOpenDeleteDialog(false);
    }
  };

  const handleCloseSnackbar = (event, reason) => { if (reason === "clickaway") return; setSnackbar({ ...snackbar, open: false }); };
  // --- DELETE ROLE LOGIC ---
  const handleRoleDeleteClick = () => setOpenRoleDeleteDialog(true);
  const handleConfirmRoleDelete = async () => {
    try {
      await Promise.all(roleSelected.map(roleId => api.delete(`/api/manage-users/roles/delete/${roleId}`)));
      fetchRoles();
      setRoleSelected([]);
      setOpenRoleDeleteDialog(false);
      setSnackbar({ open: true, message: "Role(s) deleted successfully!", severity: "success" });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Failed to delete role(s).",
        severity: "error"
      });
      setOpenRoleDeleteDialog(false);
    }
  };

  // --- ROLE MANAGEMENT LOGIC ---
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Reset selections when switching tabs
    setSelected([]);
    setRoleSelected([]);
  };

  const handleCreateRoleClick = () => {
    setRoleData({ role_name: "", permissions: [] });
    setOpenCreateRoleDialog(true);
  };

  const handleEditRoleClick = (e, role) => {
    // Stop propagation if clicking via row click (though edit button is usually separate)
    if (e) e.stopPropagation();

    setEditingRole(role);
    setRoleData({
      role_name: role.role_name || role.name,
      permissions: role.permissions || []
    });
    setOpenEditRoleDialog(true);
  };

  /*const handleRolePermissionsChange = (event) => {
    const { target: { value } } = event;
    setRoleData({
      ...roleData,
      permissions: typeof value === 'string' ? value.split(',') : value,
    });
  };
*/
  const handleCreateRoleSave = async () => {
    if (!roleData.role_name || roleData.permissions.length === 0) {
      setSnackbar({ open: true, message: "Please provide role name and select permissions", severity: "warning" });
      return;
    }

    try {
      await api.post("/api/manage-users/roles/create", roleData);
      fetchRoles();
      setOpenCreateRoleDialog(false);
      setSnackbar({ open: true, message: "Role created successfully!", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || "Failed to create role", severity: "error" });
    }
  };

  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option) => `${option.perm_id} ${option.perm_name}`,
  });

  const handleEditRoleSave = async () => {
    if (!roleData.role_name || roleData.permissions.length === 0) {
      setSnackbar({ open: true, message: "Please provide role name and select permissions", severity: "warning" });
      return;
    }

    try {
      await api.put(`/api/manage-users/roles/update/${editingRole.role_id || editingRole.id}`, {
        role_name: roleData.role_name,
        permissions: roleData.permissions
      });
      fetchRoles();
      setOpenEditRoleDialog(false);
      setSnackbar({ open: true, message: "Role updated successfully! This affects all users with this role.", severity: "warning" });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || "Failed to update role", severity: "error" });
    }
  };


  // Calculations for slicing
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredRows.length) : 0;
  const visibleRows = useMemo(() => [...filteredRows].sort(getComparator(order, orderBy)).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [order, orderBy, page, rowsPerPage, filteredRows]);

  const emptyRoleRows = rolePage > 0 ? Math.max(0, (1 + rolePage) * roleRowsPerPage - filteredRoles.length) : 0;
  const visibleRoleRows = useMemo(() => [...filteredRoles].sort(getComparator(roleOrder, roleOrderBy)).slice(rolePage * roleRowsPerPage, rolePage * roleRowsPerPage + roleRowsPerPage), [roleOrder, roleOrderBy, rolePage, roleRowsPerPage, filteredRoles]);

  // Limit for permission chips displayed 
  // Sub-component to handle individual row expansion
  const PermissionsList = ({ permissionIds = [], availablePermissions }) => {
    const [expanded, setExpanded] = useState(false);
    const LIMIT = 6;
    // Map IDs to actual objects safely
    const validPermissions = permissionIds
      .map(id => availablePermissions.find(p => String(p.perm_id) === String(id)))

    if (validPermissions.length === 0) {
      return <Typography variant="caption" color="text.secondary">No permissions</Typography>;
    }

    // Determine which items to show
    const visibleItems = expanded ? validPermissions : validPermissions.slice(0, LIMIT);
    const hiddenCount = validPermissions.length - LIMIT;

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {visibleItems.map((perm) => (
          <Chip
            key={perm.perm_id}
            label={perm.perm_name}
            size="small"
            sx={{ mr: 0.5 }}
          />
        ))}

        {/* Logic for the Toggle Button */}
        {!expanded && hiddenCount > 0 && (
          <Chip
            label={`+${hiddenCount} more`}
            size="small"
            color="primary"
            onClick={(e) => {
              e.stopPropagation(); // Prevent row selection
              setExpanded(true);
            }}
            sx={{ cursor: 'pointer', fontWeight: 'bold' }}
          />
        )}

        {expanded && validPermissions.length > LIMIT && (
          <Chip
            label="Show Less"
            size="small"
            onClick={(e) => {
              e.stopPropagation(); // Prevent row selection
              setExpanded(false);
            }}
            sx={{ cursor: 'pointer', backgroundColor: '#eee' }}
          />
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="user and role management tabs">
          <Tab label="Users" />
          <Tab label="Roles & Permissions" />
        </Tabs>
      </Box>

      {/* === USERS TAB === */}
      {tabValue === 0 && (
        <Paper sx={{ width: "100%", mb: 2, mt: 2 }}>
          <EnhancedTableToolbar
            numSelected={selected.length}
            handleDeleteClick={handleDeleteClick}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            handleAddClick={handleAddClick}
            title="MANAGE USERS"
            searchPlaceholder="Search Users..."
            showDelete={true}
          />
          <TableContainer>
            <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size={dense ? "small" : "medium"}>
              <EnhancedTableHead
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={filteredRows.length}
                cells={userHeadCells}
              />
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3 }}><CircularProgress /></TableCell></TableRow>
                ) : visibleRows.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center"><Alert severity="info">{errorMessage || "No data available."}</Alert></TableCell></TableRow>
                ) : (
                  visibleRows.map((row, index) => {
                    const isItemSelected = isSelected(row.id);
                    const labelId = `enhanced-table-checkbox-${index}`;
                    return (
                      <TableRow hover onClick={(event) => handleClick(event, row.id)} role="checkbox" aria-checked={isItemSelected} tabIndex={-1} key={row.id} selected={isItemSelected} sx={{ cursor: "pointer" }}>
                        <TableCell padding="checkbox"><Checkbox color="primary" checked={isItemSelected} inputProps={{ "aria-labelledby": labelId }} /></TableCell>
                        <TableCell component="th" id={labelId} scope="row" padding="none">{row.name || "N/A"}</TableCell>
                        <TableCell>{row.email}</TableCell>
                        <TableCell>
                          {Array.isArray(row.roles) ? row.roles.map((roleId, idx) => {
                            const role = availableRoles.find(r => String(r.role_id || r.id) === String(roleId));
                            const roleName = role ? (role.role_name || role.name) : roleId;
                            return <Chip key={idx} label={roleName} size="small" sx={{ mr: 0.5 }} />;
                          }) : <Chip label={row.roles || "User"} size="small" />}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Edit User">
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditClick(e, row);
                              }}
                              size="small"
                              color="primary"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
                {emptyRows > 0 && <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}><TableCell colSpan={6} /></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination rowsPerPageOptions={[5, 10, 25]} component="div" count={filteredRows.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
        </Paper>
      )}

      {/* === ROLES TAB === */}
      {tabValue === 1 && (
        <Paper sx={{ width: "100%", mb: 2, mt: 2 }}>
          <EnhancedTableToolbar
            numSelected={roleSelected.length}
            handleDeleteClick={handleRoleDeleteClick}
            searchQuery={roleSearchQuery}
            setSearchQuery={setRoleSearchQuery}
            handleAddClick={handleCreateRoleClick}
            title="ROLES & PERMISSIONS"
            searchPlaceholder="Search Roles..."
            showDelete={true}
          />

          <TableContainer>
            <Table sx={{ minWidth: 750 }} size={dense ? "small" : "medium"}>
              <EnhancedTableHead
                numSelected={roleSelected.length}
                order={roleOrder}
                orderBy={roleOrderBy}
                onSelectAllClick={handleRoleSelectAllClick}
                onRequestSort={handleRoleRequestSort}
                rowCount={filteredRoles.length}
                cells={roleHeadCells}
              />
              <TableBody>
                {availableRoles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Alert severity="info">No roles available.</Alert>
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleRoleRows.map((role, index) => {
                    const roleId = role.role_id || role.id;
                    const isItemSelected = isRoleSelected(roleId);
                    const labelId = `enhanced-role-checkbox-${index}`;

                    return (
                      <TableRow
                        hover
                        onClick={(event) => handleRoleClick(event, roleId)}
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={roleId}
                        selected={isItemSelected}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox color="primary" checked={isItemSelected} inputProps={{ "aria-labelledby": labelId }} />
                        </TableCell>
                        <TableCell component="th" id={labelId} scope="row" padding="none">{role.role_name || role.name}</TableCell>
                        <TableCell>
                          <PermissionsList permissionIds={role.permissions} availablePermissions={availablePermissions} />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Edit Role">
                            <IconButton onClick={(e) => handleEditRoleClick(e, role)} color="primary" size="small">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
                {emptyRoleRows > 0 && <TableRow style={{ height: (dense ? 33 : 53) * emptyRoleRows }}><TableCell colSpan={4} /></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredRoles.length}
            rowsPerPage={roleRowsPerPage}
            page={rolePage}
            onPageChange={handleChangeRolePage}
            onRowsPerPageChange={handleChangeRoleRowsPerPage}
          />
        </Paper>
      )}

      {/* --- CREATE USER DIALOG --- */}
      <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)}>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Fill in the details to create a new user.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            variant="outlined"
            value={createData.name}
            onChange={(e) => setCreateData((prev) => ({ ...prev, name: e.target.value }))}
          />
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={createData.email}
            onChange={(e) => setCreateData((prev) => ({ ...prev, email: e.target.value }))}
          />
          <TextField
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            variant="outlined"
            value={createData.password}
            onChange={(e) => setCreateData((prev) => ({ ...prev, password: e.target.value }))}
          />

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="create-roles-label">Roles</InputLabel>
            <Select
              labelId="create-roles-label"
              multiple
              value={createData.roles}
              onChange={handleCreateRolesChange}
              input={<OutlinedInput label="Roles" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((roleId) => {
                    const role = availableRoles.find(r => String(r.role_id) === String(roleId));
                    const roleName = role ? (role.role_name || role.name) : roleId;
                    return <Chip key={roleId} label={roleName} />;
                  })}
                </Box>
              )}
              MenuProps={MenuProps}
            >
              {availableRoles.map((role) => {
                const rId = role.id || role.roleId || role.role_id;
                const rName = role.name || role.roleName || role.role_name;
                return (
                  <MenuItem key={rId} value={rId}>
                    <Checkbox checked={createData.roles.some(r => String(r) === String(rId))} />
                    <ListItemText primary={`${rName}`} />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateClick} variant="contained" color="primary">Create</Button>
        </DialogActions>
      </Dialog>

      {/* --- EDIT DIALOG --- */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)}>
        <DialogTitle>Edit User Role</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>Update user roles. (Email cannot be changed).</DialogContentText>
          <TextField margin="dense" label="Email" fullWidth variant="outlined" value={editData.email} disabled />
          <TextField margin="dense" label="Name" fullWidth variant="outlined" value={editData.name} disabled sx={{ mb: 2 }} />

          <FormControl fullWidth>
            <InputLabel id="roles-label">Roles</InputLabel>
            <Select
              labelId="roles-label"
              id="roles-select"
              multiple
              value={editData.roles}
              onChange={handleEditRolesChange}
              input={<OutlinedInput label="Roles" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((roleId) => {
                    const role = availableRoles.find(r => String(r.role_id || r.id) === String(roleId));
                    const roleName = role ? (role.role_name || role.name) : roleId;
                    return <Chip key={roleId} label={roleName} />;
                  })}
                </Box>
              )}
              MenuProps={MenuProps}
            >
              {availableRoles.map((role) => {
                const rId = role.id || role.roleId || role.role_id;
                const rName = role.name || role.roleName || role.role_name;
                const displayText = rName ? `${rName}` : JSON.stringify(role);

                return (
                  <MenuItem key={rId} value={rId}>
                    <Checkbox checked={editData.roles.some(r => String(r) === String(rId))} />
                    <ListItemText primary={displayText} />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained" color="primary">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog (Bulk Only) */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent><DialogContentText>Are you sure you want to delete the selected {selected.length} user(s)? This action cannot be undone.</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Role Dialog */}
      <Dialog open={openRoleDeleteDialog} onClose={() => setOpenRoleDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent><DialogContentText>Are you sure you want to delete the selected {roleSelected.length} role(s)? This action cannot be undone.</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRoleDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmRoleDelete} color="error" autoFocus>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* --- CREATE ROLE DIALOG --- */}
      <Dialog open={openCreateRoleDialog} onClose={() => setOpenCreateRoleDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Role</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Create a new role with selected permissions.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Role Name"
            fullWidth
            variant="outlined"
            value={roleData.role_name}
            onChange={(e) => setRoleData({ ...roleData, role_name: e.target.value })}
          />

          <Autocomplete
            multiple
            options={(availablePermissions)}
            filterOptions={filterOptions}

            getOptionLabel={(option) => `${option.perm_id}`}
            value={availablePermissions.filter(perm =>
              roleData.permissions.some(p => String(p) === String(perm.perm_id))
            )}
            onChange={(event, newValue) => {
              const clickedAddNew = newValue.find(item => item.perm_id === "");

              if (clickedAddNew) {
                setOpenCreatePermDialog(true);
                return;
              }
              setRoleData({
                ...roleData,
                permissions: newValue.map(perm => perm.perm_id)
              });
            }}

            renderInput={(params) => (
              <TextField {...params} label="Permissions" placeholder="Type to search permissions..." />
            )}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox style={{ marginRight: 8 }} checked={selected} />
                {`${option.perm_id}: ${option.perm_name}`}
              </li>

            )}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.perm_id}
                  label={`${option.perm_id}`}
                  size="small"
                />
              ))
            }
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateRoleDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateRoleSave} variant="contained" color="primary">Create Role</Button>
        </DialogActions>
      </Dialog>

      {/* --- EDIT ROLE DIALOG --- */}
      <Dialog open={openEditRoleDialog} onClose={() => setOpenEditRoleDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Role</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Update role permissions. <strong>Warning:</strong> This will affect ALL users with this role.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Role Name"
            fullWidth
            variant="outlined"
            value={roleData.role_name}
            onChange={(e) => setRoleData({ ...roleData, role_name: e.target.value })}
          />

          <Autocomplete
            multiple
            options={(availablePermissions)}
            filterOptions={filterOptions}

            getOptionLabel={(option) => `${option.perm_id}`}
            value={availablePermissions.filter(perm =>
              roleData.permissions.some(p => String(p) === String(perm.perm_id))
            )}
            onChange={(event, newValue) => {
              const clickedAddNew = newValue.find(item => item.perm_id === "");
              setRoleData({
                ...roleData,
                permissions: newValue.map(perm => perm.perm_id)
              });
            }}

            renderInput={(params) => (
              <TextField {...params} label="Permissions" placeholder="Type to search permissions..." />
            )}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox style={{ marginRight: 8 }} checked={selected} />
                {`${option.perm_id}: ${option.perm_name}`}
              </li>
            )}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.perm_id}
                  label={`${option.perm_id}`}
                  size="small"
                />
              ))
            }
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditRoleDialog(false)}>Cancel</Button>
          <Button onClick={handleEditRoleSave} variant="contained" color="warning">Update Role (Global Change)</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}